import { API_ENDPOINTS } from '@/api/constant/endpoints';

export type OAuthProvider = 'google' | 'microsoft';
export type OAuthIntent = 'login' | 'signup';

export const OAUTH_PROVIDERS: readonly OAuthProvider[] = ['google', 'microsoft'] as const;

export interface IOAuthStartParams {
    readonly intent: OAuthIntent;
    /**
     * Persist the session beyond the browser session — the login form's
     * "Remember me". The API stores it in the OAuth state and applies it when
     * the session is finally issued, so the cookie lifetime matches what a
     * password login with the same checkbox would get.
     */
    readonly rememberMe: boolean;
    /** Plan carried from the pricing page (`?plan=`) — starts the trial on it. */
    readonly planSlug?: string;
    readonly billingPeriod?: 'monthly' | 'yearly';
}

/**
 * Builds the full-page navigation target that begins an OAuth sign-in:
 * `{api}/auth/oauth/{provider}/start?intent=…&remember=1|0[&plan=…&billing=…]`.
 * The API answers with a 302 to the provider, so this is a plain link, never an
 * XHR. Pure so the query shape is testable without a DOM or a base URL.
 */
export function buildOAuthStartUrl(
    apiBaseUrl: string,
    provider: OAuthProvider,
    params: IOAuthStartParams,
): string {
    const base = apiBaseUrl.replace(/\/+$/, '');
    const query = new URLSearchParams({
        intent: params.intent,
        remember: params.rememberMe ? '1' : '0',
    });
    // Only appended when present: an absent plan means "default-plan trial",
    // and the API treats an empty `plan=` as malformed rather than absent.
    if (params.planSlug) query.set('plan', params.planSlug);
    if (params.billingPeriod) query.set('billing', params.billingPeriod);
    return `${base}${API_ENDPOINTS.AUTH.OAUTH_START(provider)}?${query.toString()}`;
}
